import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc
} from 'firebase/firestore';
import Container from '../../components/layout/Container/Container';
import Card, { CardBody } from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import Alert from '../../components/ui/Alert/Alert';
import { Form, FormGroup, FormLabel, FormInput, FormTextarea, FormCheck } from '../../components/ui/Form/Form';
import { db, serverTimestamp } from '../../services/firebase';
import { useAuth } from '../../features/auth/context/AuthContext';
import './AdminDashboard.css';

const initialForm = {
  title: '',
  excerpt: '',
  content: '',
  coverImage: '',
  tags: '',
  isPublished: false
};

const formatDate = (timestamp) => {
  if (!timestamp || typeof timestamp.toDate !== 'function') {
    return 'Not published';
  }

  return timestamp.toDate().toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const AdminDashboard = () => {
  const [formData, setFormData] = useState(initialForm);
  const [posts, setPosts] = useState([]);
  const [activeSection, setActiveSection] = useState('editor');
  const [editingPostId, setEditingPostId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { user, logout } = useAuth();

  useEffect(() => {
    const postsQuery = query(collection(db, 'posts'), orderBy('updatedAt', 'desc'));
    const unsubscribe = onSnapshot(
      postsQuery,
      (snapshot) => {
        const nextPosts = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data()
        }));
        setPosts(nextPosts);
        setLoadingPosts(false);
      },
      (err) => {
        setError(`Unable to load admin posts. ${err?.message || ''}`.trim());
        setLoadingPosts(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const publishedCount = useMemo(
    () => posts.filter((post) => post.isPublished).length,
    [posts]
  );

  const resetForm = () => {
    setFormData(initialForm);
    setEditingPostId('');
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage('');
    setError('');

    try {
      const tags = formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);

      const payload = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        content: formData.content.trim(),
        coverImage: formData.coverImage.trim(),
        tags,
        isPublished: formData.isPublished,
        updatedAt: serverTimestamp(),
        publishedAt: formData.isPublished ? serverTimestamp() : null,
        authorUid: user?.uid || null,
        authorEmail: user?.email || null
      };

      if (editingPostId) {
        await updateDoc(doc(db, 'posts', editingPostId), payload);
        setMessage(formData.isPublished ? 'Post updated successfully.' : 'Draft updated successfully.');
      } else {
        await addDoc(collection(db, 'posts'), {
          ...payload,
          createdAt: serverTimestamp()
        });
        setMessage(formData.isPublished ? 'Post published successfully.' : 'Draft saved successfully.');
      }

      resetForm();
      setActiveSection('posts');
    } catch (err) {
      const nextMessage = err?.code === 'permission-denied'
        ? `Firestore denied this save for UID ${user?.uid || 'unknown'}. Make sure firestore.rules uses this same UID, then deploy the rules again.`
        : `Unable to save post. ${err?.message || 'Please try again.'}`;
      setError(nextMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (post) => {
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      coverImage: post.coverImage || '',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
      isPublished: !!post.isPublished
    });
    setEditingPostId(post.id);
    setMessage('');
    setError('');
    setActiveSection('editor');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (postId) => {
    setMessage('');
    setError('');

    try {
      await deleteDoc(doc(db, 'posts', postId));
      if (editingPostId === postId) {
        resetForm();
      }
      setMessage('Post deleted successfully.');
    } catch (err) {
      setError(`Unable to delete post. ${err?.message || 'Please try again.'}`);
    }
  };

  const togglePublishedState = async (post) => {
    setMessage('');
    setError('');

    try {
      await updateDoc(doc(db, 'posts', post.id), {
        isPublished: !post.isPublished,
        updatedAt: serverTimestamp(),
        publishedAt: !post.isPublished ? serverTimestamp() : null
      });
      setMessage(!post.isPublished ? 'Post published successfully.' : 'Post moved to draft.');
    } catch (err) {
      setError(`Unable to update post status. ${err?.message || 'Please try again.'}`);
    }
  };

  return (
    <div className="admin-dashboard-page bg-light">
      <Container>
        <div className="admin-dashboard-header d-flex flex-wrap align-items-center justify-content-between mb-4">
          <div>
            <p className="text-uppercase text-muted small mb-1">Admin Dashboard</p>
            <h1 className="h3 fw-bold mb-0">Post Management</h1>
            <p className="text-muted mb-0 mt-2">
              Signed in as {user?.email || 'unknown'} ({user?.uid || 'no uid'})
            </p>
          </div>
          <Button variant="outline-secondary" onClick={logout}>
            Log out
          </Button>
        </div>

        <Card className="shadow-sm border-0 mb-4">
          <CardBody className="admin-nav">
            <div className="admin-nav-links">
              <button
                type="button"
                className={`admin-nav-link ${activeSection === 'editor' ? 'active' : ''}`}
                onClick={() => setActiveSection('editor')}
              >
                {editingPostId ? 'Edit Post' : 'New Post'}
              </button>
              <button
                type="button"
                className={`admin-nav-link ${activeSection === 'posts' ? 'active' : ''}`}
                onClick={() => setActiveSection('posts')}
              >
                Manage Posts
              </button>
            </div>

            <div className="admin-summary">
              <div className="admin-summary-item">
                <strong>{posts.length}</strong>
                <span>Total Posts</span>
              </div>
              <div className="admin-summary-item">
                <strong>{publishedCount}</strong>
                <span>Published</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {message && <Alert variant="success" className="mb-3">{message}</Alert>}
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        {activeSection === 'editor' && (
          <Card className="shadow-sm border-0 mb-4">
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <div>
                  <h2 className="h4 fw-bold mb-1">{editingPostId ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
                  <p className="text-muted mb-0">Write, publish, and update posts from one admin workspace.</p>
                </div>
                {editingPostId && (
                  <Button variant="outline-secondary" onClick={resetForm}>
                    Cancel Edit
                  </Button>
                )}
              </div>

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <FormLabel htmlFor="title">Title</FormLabel>
                  <FormInput
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Post title"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="excerpt">Excerpt</FormLabel>
                  <FormTextarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    placeholder="Short summary for the blog list"
                    rows={3}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="content">Content</FormLabel>
                  <FormTextarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="Write the full post content here..."
                    rows={10}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="coverImage">Cover Image URL</FormLabel>
                  <FormInput
                    id="coverImage"
                    name="coverImage"
                    value={formData.coverImage}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="tags">Tags</FormLabel>
                  <FormInput
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    placeholder="firebase, react, blog"
                  />
                </FormGroup>

                <FormGroup className="mb-4">
                  <FormCheck
                    id="isPublished"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleChange}
                    label="Publish immediately"
                  />
                </FormGroup>

                <div className="d-flex flex-wrap gap-2">
                  <Button type="submit" variant="primary" disabled={submitting}>
                    {submitting ? 'Saving...' : editingPostId ? 'Update Post' : 'Save Post'}
                  </Button>
                  <Button type="button" variant="outline-secondary" onClick={resetForm}>
                    Clear Form
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        )}

        {activeSection === 'posts' && (
          <Card className="shadow-sm border-0">
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <div>
                  <h2 className="h4 fw-bold mb-1">Manage Posts</h2>
                  <p className="text-muted mb-0">Edit, publish, unpublish, and delete your blog posts.</p>
                </div>
                <Button variant="primary" onClick={() => setActiveSection('editor')}>
                  Create New Post
                </Button>
              </div>

              {loadingPosts && <p className="text-muted mb-0">Loading posts...</p>}

              {!loadingPosts && posts.length === 0 && (
                <p className="text-muted mb-0">No posts found yet. Create your first post from the editor tab.</p>
              )}

              {!loadingPosts && posts.length > 0 && (
                <div className="admin-post-list">
                  {posts.map((post) => (
                    <div key={post.id} className="admin-post-item">
                      <div className="admin-post-main">
                        <div className="admin-post-top">
                          <h3 className="h5 fw-bold mb-1">{post.title || 'Untitled post'}</h3>
                          <span className={`admin-status ${post.isPublished ? 'published' : 'draft'}`}>
                            {post.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        <p className="text-muted mb-2">{post.excerpt || 'No excerpt provided.'}</p>
                        <div className="admin-post-meta">
                          <span>Updated: {formatDate(post.updatedAt)}</span>
                          <span>Published: {formatDate(post.publishedAt)}</span>
                          <span>Tags: {Array.isArray(post.tags) && post.tags.length > 0 ? post.tags.join(', ') : 'None'}</span>
                        </div>
                      </div>

                      <div className="admin-post-actions">
                        <Button variant="outline-primary" onClick={() => startEdit(post)}>
                          Edit
                        </Button>
                        <Button
                          variant={post.isPublished ? 'outline-warning' : 'outline-success'}
                          onClick={() => togglePublishedState(post)}
                        >
                          {post.isPublished ? 'Unpublish' : 'Publish'}
                        </Button>
                        <Button variant="outline-danger" onClick={() => handleDelete(post.id)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default AdminDashboard;
